/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import Preloader from 'components/Preloader';
import React, { useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
    DatePicker, NumberInput, Title, TextInput, Select,
} from 'ui-library';

import { compareDates } from 'utils/datetime';
import { validateIban } from 'utils/validation';
import { isQRIban } from 'utils';
import { DEFAULT_APP_CURRENCY } from 'constants/constants';

const WithdrawalPlanComp = (props) => {
    const { t } = useTranslation();
    const {
        data,
        setData,
        errorData,
        withdrawalOption,
        optionWithdrawalPeriodicity,
        isLoading,
        isOptionsLoading,
        trigger,
        setErrorData,
        control,
    } = props || {};

    const withdrawalPeriodicityOptions = useMemo(() => optionWithdrawalPeriodicity?.map((option) => ({ value: option?.id, label: option?.label })), [optionWithdrawalPeriodicity]);

    const handleFrequencyOnChange = useCallback((val) => {
        if (val !== withdrawalOption?.periodicity) {
            setData({
                ...data, frequency: val, startDate: moment(withdrawalOption?.startDate)?.format('MM/YYYY'), withdrawalsStartDate: moment(withdrawalOption?.startDate)?.format('MM/YYYY'),
            });
        } else {
            setData({
                ...data, frequency: val,
            });
        }
    }, [withdrawalOption?.periodicity, withdrawalOption?.startDate, data]);

    return (
        <div>
            <Preloader isLoading={isLoading || isOptionsLoading} >
                <div className="shopping-cart--withdrawalPlanGrid">
                    <div className="form">
                        <fieldset className="setup-section">
                            <div className="form">
                                <Controller
                                    name="amount"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <NumberInput
                                            {...field}
                                            placeholder={t('3b.withdrawalPlan.withdrawalAmountPlaceholder')}
                                            thousandSeparator="’"
                                            decimalSeparator="."
                                            label={t('3b.withdrawalPlan.amount')}
                                            prefix={`${DEFAULT_APP_CURRENCY} `}
                                            value={data?.amount}
                                            onChange={(value: number) => {
                                                onChange(value);
                                                trigger('amount');
                                            }}
                                            error={errorData?.amount?.message}
                                            required
                                            allowNegativeValue={false}
                                            textRight
                                        />
                                    )}
                                />
                                <Controller
                                    name="frequency"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <Select
                                            {...field}
                                            /* @ts-ignore */
                                            placeholder={t('3b.kyc.pleaseSelect')}
                                            label={t('3b.withdrawalPlan.frequency')}
                                            value={data?.frequency}
                                            options={withdrawalPeriodicityOptions}
                                            error={errorData?.frequency?.message}
                                            required
                                            onChange={withdrawalOption?.periodicity ? handleFrequencyOnChange : onChange}
                                        />
                                    )}
                                />
                                <Controller
                                    name="startDate"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <DatePicker
                                            {...field}
                                            /* @ts-ignore */
                                            label={t('3b.withdrawalPlan.withdrawalsStart')}
                                            value={data?.startDate || null}
                                            disabledDate={(current) => current && (compareDates(new Date(), current))}
                                            onChange={(value) => {
                                                const date = moment(value).format('MM/YYYY');

                                                onChange(date);
                                            }}
                                            placeholder="MM/YYYY"
                                            error={errorData?.startDate?.message}
                                            format="MM/YYYY"
                                            picker="month"
                                            required={!(withdrawalOption?.periodicity && data?.frequency && withdrawalOption?.periodicity !== +data?.frequency)}
                                            disabled={(withdrawalOption?.periodicity && data?.frequency && withdrawalOption?.periodicity !== +data?.frequency)}
                                        />
                                    )}
                                />
                            </div>
                        </fieldset>

                        <fieldset className="bank-section">
                            <Title type={3} tagName="legend">
                                {t('3b.withdrawalPlan.bankTitle')}
                            </Title>
                            <div className="form">
                                <Controller
                                    name="iban"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="iban"
                                            label={t('3b.withdrawalPlan.bankIban')}
                                            value={data?.iban}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                onChange(e.target.value);
                                                setErrorData('iban', { message: '' });
                                            }}
                                            error={errorData?.iban?.message}
                                            onBlur={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                setErrorData('iban', {
                                                    message: await validateIban(e?.target?.value) as string,
                                                });
                                            }}
                                            required
                                        />
                                    )}
                                />

                                <Controller
                                    name="esr"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="esr"
                                            label={t('3b.withdrawalPlan.esr')}
                                            value={data?.esr}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                onChange(e.target.value);
                                                setErrorData('esr', { message: '' });
                                            }}
                                            error={errorData?.esr?.message}
                                            required={isQRIban(data?.iban)}
                                            labelInfo={t('3b.withdrawalPlan.esr.info')}
                                        />
                                    )}
                                />

                                <Controller
                                    name="firstName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            value={data?.firstName}
                                            data-cy="firstName"
                                            label={t('clientDetails.firstName')}
                                            error={errorData?.firstName?.message}
                                            required
                                        />
                                    )}
                                />

                                <Controller
                                    name="lastName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="lastName"
                                            value={data?.lastName}
                                            label={t('clientDetails.lastName')}
                                            error={errorData?.lastName?.message}
                                            required
                                        />
                                    )}
                                />

                                <Controller
                                    name="bankName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="bankName"
                                            value={data?.bankName}
                                            label={t('3b.withdrawalPlan.bankName')}
                                            error={errorData?.bankName?.message}
                                            required
                                        />
                                    )}
                                />

                                <div className="shopping-cart--withdrawal-options-bankZip">
                                    <div className="form-group">
                                        <label>{`${t('3b.withdrawalPlan.bankZipCode')}/${t('3b.withdrawalPlan.bankCity')} *`}</label>
                                        <div className="shopping-cart--withdrawal-options-bankZip-inputs">
                                            <Controller
                                                name="zipCode"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <TextInput
                                                        {...field}
                                                        value={data?.zipCode}
                                                        data-cy="zipCode"
                                                        error={errorData?.zipCode?.message}
                                                        onChange={(value: number) => {
                                                            onChange(value);
                                                            trigger('zipCode');
                                                        }}
                                                        required
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="city"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextInput
                                                        {...field}
                                                        value={data?.city}
                                                        data-cy="city"
                                                        error={errorData?.city?.message}
                                                        required
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </fieldset>
                    </div>
                </div>
            </Preloader>
        </div>
    );
};

export default WithdrawalPlanComp;
